@layer refine.base {
  :host {
    --_day-selected-bg: var(--calendar-day-selected-bg, var(--_theme-focus));
    --_day-today-color: var(--calendar-day-today-color, var(--_theme-focus));
    --_day-outside-opacity: var(--calendar-day-outside-opacity, 0.35);
    --_bg: var(--calendar-bg, var(--color-canvas));
    --_border-color: var(--calendar-border-color, var(--color-contrast-200));
    --_radius: var(--calendar-radius, var(--rounded-xl));

    display: inline-block;
  }

  :host([fullwidth]) {
    display: block;
  }

  :host([color='secondary']) {
    --_day-selected-bg: var(--calendar-day-selected-bg, var(--_theme-base));
    --_day-today-color: var(--calendar-day-today-color, var(--_theme-base));
  }

  /* ── Root panel ──────────────────────────────────────────────────────────── */

  .calendar {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    min-width: 18rem;
    padding: var(--size-3);
    background: var(--_bg);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--calendar-shadow, var(--shadow-md));
  }

  :host([fullwidth]) .calendar {
    min-width: 0;
  }

  /* ── Header ──────────────────────────────────────────────────────────────── */

  .cal-header {
    display: flex;
    gap: var(--size-1);
    align-items: center;
    justify-content: space-between;
    padding-block-end: var(--size-2);
    border-block-end: var(--border) solid var(--_border-color);
  }

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size-8);
    height: var(--size-8);
    padding: 0;
    color: var(--color-contrast-600);
    cursor: pointer;
    outline: none;
    background: transparent;
    border: none;
    border-radius: var(--rounded-md);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .nav-btn:hover:not(:disabled) {
    color: var(--color-contrast-900);
    background: var(--color-contrast-300);
  }

  .nav-btn:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .nav-btn:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  .cal-label-btn {
    flex: 1;
    padding: var(--size-1) var(--size-2);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-900);
    text-align: center;
    cursor: pointer;
    outline: none;
    background: transparent;
    border: none;
    border-radius: var(--rounded-md);
    transition: background var(--transition-fast);
  }

  .cal-label-btn:hover:not(:disabled) {
    background: var(--color-contrast-300);
  }

  .cal-label-btn:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-label-btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .cal-label-month,
  .cal-label-year {
    display: inline-block;
  }

  .cal-label-sep {
    display: inline-block;
    padding-inline: var(--size-1);
    font-weight: var(--font-normal);
    color: var(--color-contrast-400);
  }

  /* ── Grid base ───────────────────────────────────────────────────────────── */

  [hidden] {
    display: none;
  }

  .cal-grid {
    display: flex;
    flex-direction: column;
    gap: var(--size-0-5);
  }

  .cal-grid-days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: var(--size-0-5);

    /* Reserve max height (6 week rows + 1 header) so the panel never shrinks
       on 5-week months or when switching to month/year view. */
    min-height: calc(7 * var(--size-10) + 6 * var(--size-0-5));
  }

  /* role="row" wrappers exist for ARIA grid structure only — `display: contents`
     removes them from layout so their children still participate directly in the
     parent's CSS grid tracks. */
  .cal-grid [role='row'] {
    display: contents;
  }

  .cal-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    cursor: pointer;
    outline: none;
    border-radius: var(--rounded-md);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  /* ── Column headers ──────────────────────────────────────────────────────── */

  .cal-cell-head {
    height: var(--size-8);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-contrast-500);
    cursor: default;
  }

  /* ── Day cells ───────────────────────────────────────────────────────────── */

  .cal-cell-day {
    flex-direction: column;
    gap: var(--size-0-5);
    min-width: 0;
    height: var(--size-10);
    overflow: hidden;
    color: var(--color-contrast-800);
  }

  .cal-cell-day:hover:not([data-disabled]) {
    color: var(--color-contrast-900);
    background: color-mix(in oklch, var(--_day-selected-bg) 15%, transparent);
  }

  .cal-cell-day:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-cell-day[data-today] {
    font-weight: var(--font-semibold);
    color: var(--_day-today-color);
  }

  .cal-cell-day[data-selected] {
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    background: var(--_day-selected-bg);
  }

  .cal-cell-day[data-outside] {
    opacity: var(--_day-outside-opacity);
  }

  .cal-cell-day[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.3;
  }

  /* ── Event dots (normal mode) ────────────────────────────────────────────── */

  .cal-dots {
    display: flex;
    gap: 2px;
    align-items: center;
    justify-content: center;
    margin-block-start: var(--size-0-5);
  }

  .cal-dot {
    --badge-bg: var(--_day-selected-bg);
    --badge-border-color: var(--_day-selected-bg);
  }

  .cal-dot-overflow {
    font-size: 8px;
    font-weight: var(--font-semibold);
    line-height: 1;
    color: var(--color-contrast-500);
  }

  /* ── Month / year grids ──────────────────────────────────────────────────── */

  .cal-grid-months,
  .cal-grid-years {
    /* 4-column grid — same width as cal-grid-days, natural height. */
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--size-1);
  }

  .cal-cell-month,
  .cal-cell-year {
    height: var(--size-10);
    font-size: var(--text-sm);
    color: var(--color-contrast-800);
    cursor: pointer;
    border-radius: var(--rounded-md);
  }

  .cal-cell-month:hover:not([data-disabled]),
  .cal-cell-year:hover:not([data-disabled]) {
    background: color-mix(in oklch, var(--_day-selected-bg) 15%, transparent);
  }

  .cal-cell-month:focus-visible,
  .cal-cell-year:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-cell-month[data-selected],
  .cal-cell-year[data-selected] {
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    background: var(--_day-selected-bg);
  }

  .cal-cell-month[data-disabled],
  .cal-cell-year[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.3;
  }

  /* ── Expanded variant ────────────────────────────────────────────────────── */

  :host([expanded]) {
    display: block;
    width: 100%;
  }

  :host([expanded]) .calendar {
    width: 100%;
    min-width: 0;
    padding: 0;
    overflow: hidden;
  }

  :host([expanded]) .cal-header {
    padding: var(--size-3) var(--size-4);
  }

  :host([expanded]) .cal-label-btn {
    font-size: var(--text-base);
  }

  :host([expanded]) .cal-grid {
    gap: 0;
  }

  :host([expanded]) .cal-grid-days {
    gap: 0;
    border-block-start: var(--border) solid var(--_border-color);
  }

  :host([expanded]) .cal-cell {
    border-radius: 0;
  }

  :host([expanded]) .cal-cell-head {
    height: var(--size-9);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-block-end: var(--border) solid var(--_border-color);
  }

  :host([expanded]) .cal-cell-day {
    position: relative;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    justify-content: flex-start;
    height: auto;
    min-height: var(--calendar-expanded-cell-height, var(--size-28));
    aspect-ratio: auto;
    padding: var(--size-2);
    font-size: var(--text-sm);
    vertical-align: top;
    border-block-end: var(--border) solid var(--_border-color);
    border-inline-end: var(--border) solid var(--_border-color);
  }

  /* Remove right border on last column */
  :host([expanded]) .cal-cell-day:nth-child(7n) {
    border-inline-end: none;
  }

  :host([expanded]) .cal-cell-day:hover:not([data-disabled]) {
    background: color-mix(in oklch, var(--_day-selected-bg) 6%, transparent);
  }

  /* Selected day — circle around the number only */
  :host([expanded]) .cal-cell-day[data-selected] {
    color: inherit;
    background: transparent;
  }

  :host([expanded]) .cal-cell-day[data-selected]::before {
    position: absolute;
    top: var(--size-1-5);
    left: var(--size-1-5);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size-7);
    height: var(--size-7);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    content: attr(data-day);
    background: var(--_day-selected-bg);
    border-radius: var(--rounded-full);
  }

  /* Today — circle in accent colour */
  :host([expanded]) .cal-cell-day[data-today] {
    font-weight: inherit;
    color: inherit;
  }

  :host([expanded]) .cal-cell-day[data-today]::before {
    position: absolute;
    top: var(--size-1-5);
    left: var(--size-1-5);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size-7);
    height: var(--size-7);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    content: attr(data-day);
    background: var(--_day-today-color);
    border-radius: var(--rounded-full);
  }

  /* When both today AND selected — selected wins */
  :host([expanded]) .cal-cell-day[data-today][data-selected]::before {
    background: var(--_day-selected-bg);
  }

  /* ── Expanded event pills ────────────────────────────────────────────────── */

  /* Push events below the day-number circle row */
  :host([expanded]) .cal-events {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    margin-block-start: var(--size-4);
    overflow: hidden;
  }

  :host([expanded]) .cal-event-pill {
    --badge-max-width: 100%;
    max-width: 100%;
  }

  :host([expanded]) .cal-event-pill-overflow {
    padding-inline: var(--size-1);
    font-size: 10px;
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
  }

  /* Dots are hidden in expanded mode — pills take over */
  :host([expanded]) .cal-dots {
    display: none;
  }

  :host([expanded]) .cal-cell-day[data-outside] {
    background: color-mix(in oklch, var(--color-contrast-900) 2%, transparent);
  }

  /* Expanded month / year grids */
  :host([expanded]) .cal-grid-months,
  :host([expanded]) .cal-grid-years {
    gap: var(--size-2);
    min-height: 0;
    padding: var(--size-4);
  }

  :host([expanded]) .cal-cell-month,
  :host([expanded]) .cal-cell-year {
    min-height: var(--size-16);
    font-size: var(--text-base);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--rounded-lg);
  }

  :host([expanded]) .cal-cell-month[data-selected],
  :host([expanded]) .cal-cell-year[data-selected] {
    border-color: transparent;
  }

  /* ── Disabled host ───────────────────────────────────────────────────────── */

  :host(.is-disabled) .calendar {
    pointer-events: none;
    opacity: 0.6;
  }

  /* ── Reduced motion ──────────────────────────────────────────────────────── */

  @media (prefers-reduced-motion: reduce) {
    .nav-btn,
    .cal-label-btn,
    .cal-cell {
      transition: none;
    }
  }
}
